import { computed, onMounted, reactive, ref } from "vue";
import { Modal } from "bootstrap";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import incomeService, { type IncomeCategory } from "@/services/income";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type IncomeFormState = {
  category_id: string;
  amount: string;
  description: string;
  warehouse_id: string;
  status: string;
};

type IncomeCategoryForm = {
  code: string;
  name: string;
  description: string;
};

const createInitialForm = (): IncomeFormState => ({
  category_id: "",
  amount: "",
  description: "",
  warehouse_id: "",
  status: "1",
});

const createInitialCategoryForm = (): IncomeCategoryForm => ({
  code: "",
  name: "",
  description: "",
});

const getPhnomPenhISOString = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Phnom_Penh",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);
  const getPart = (type: string) => parts.find((part) => part.type === type)?.value;

  return `${getPart("year")}-${getPart("month")}-${getPart("day")}T${getPart("hour")}:${getPart("minute")}`;
};

export function useIncomeForm() {
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();

  const referenceno = ref("");
  const date = ref(getPhnomPenhISOString(new Date()));
  const categories = ref<IncomeCategory[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const isLoading = ref(false);
  const isCategorySaving = ref(false);

  const form = reactive<IncomeFormState>(createInitialForm());
  const categoryForm = reactive<IncomeCategoryForm>(createInitialCategoryForm());
  const incomeId = computed(() => Number(route.params.id || 0));
  const isEditMode = computed(() => Boolean(incomeId.value));
  const isCategoryEditMode = computed(() => false);

  const fetchNextRef = async () => {
    if (isEditMode.value) return;
    try {
      referenceno.value = await incomeService.getNextReference();
    } catch (error) {
      console.error("Error fetching next income reference:", error);
    }
  };

  const fetchBaseData = async () => {
    const [categoryData, warehouseData] = await Promise.all([
      incomeService.getIncomeCategories(),
      warehouseService.getWarehouses(),
    ]);

    categories.value = categoryData;
    warehouses.value = warehouseData;
  };

  const generateCategoryCode = () => {
    const normalizedCodes = new Set(categories.value.map((item) => String(item.code || "").trim().toUpperCase()));
    let nextNumber = normalizedCodes.size + 1;
    while (normalizedCodes.has(`INC-${String(nextNumber).padStart(3, "0")}`)) {
      nextNumber += 1;
    }
    categoryForm.code = `INC-${String(nextNumber).padStart(3, "0")}`;
  };

  const openCategoryModal = () => {
    Object.assign(categoryForm, createInitialCategoryForm());
    generateCategoryCode();
  };

  const updateCategoryField = (field: keyof IncomeCategoryForm, value: string) => {
    categoryForm[field] = value;
  };

  const hideCategoryModal = () => {
    const modalElement = document.getElementById("incomeCategoryModal");
    if (!modalElement) return;

    const BootstrapModal = Modal as any;
    const modalInstance =
      BootstrapModal.getInstance?.(modalElement) ||
      BootstrapModal.getOrCreateInstance?.(modalElement) ||
      new Modal(modalElement);

    modalInstance.hide();
  };

  const submitCategory = async () => {
    if (!categoryForm.code.trim() || !categoryForm.name.trim()) {
      await Swal.fire({ icon: "warning", title: t("income_category_page.missing_fields"), text: t("income_category_page.code_and_name_required") });
      return;
    }

    isCategorySaving.value = true;
    try {
      const createdCategory = await incomeService.createIncomeCategory({
        code: categoryForm.code.trim(),
        name: categoryForm.name.trim(),
        description: categoryForm.description.trim(),
        status: 1,
      });

      await fetchBaseData();
      form.category_id = String(createdCategory.id ?? "");
      hideCategoryModal();

      await Swal.fire({
        icon: "success",
        title: t("income_category_page.category_created"),
        text: t("income_page.category_created_and_selected"),
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error: any) {
      console.error("Error creating income category:", error);
      await Swal.fire({
        icon: "error",
        title: t("income_category_page.save_failed"),
        text: error?.response?.data?.message || t("income_category_page.failed_to_save_income_category"),
      });
    } finally {
      isCategorySaving.value = false;
    }
  };

  const fetchIncomeDetails = async () => {
    if (!incomeId.value) return;

    isLoading.value = true;
    try {
      const income = await incomeService.getIncome(incomeId.value);
      referenceno.value = income.ref;
      if (income.date) {
        date.value = getPhnomPenhISOString(new Date(income.date));
      }
      form.category_id = income.category_id ? String(income.category_id) : "";
      form.amount = String(income.amount ?? "");
      form.description = income.description || "";
      form.warehouse_id = income.warehouse_id ? String(income.warehouse_id) : "";
      form.status = String(income.status ?? 1);
    } catch (error) {
      console.error("Error fetching income details:", error);
      await Swal.fire(t("income_page.error"), t("income_page.failed_to_fetch_income_details"), "error");
      router.push("/income-list");
    } finally {
      isLoading.value = false;
    }
  };

  const submitIncome = async () => {
    if (!referenceno.value.trim() || !form.category_id || !form.amount || !form.warehouse_id) {
      await Swal.fire(t("income_page.error"), t("income_page.required_fields_message"), "error");
      return;
    }

    isLoading.value = true;
    try {
      const category = categories.value.find((item) => String(item.id) === form.category_id);

      const payload = {
        ref: referenceno.value.trim(),
        date: date.value,
        amount: Number(form.amount),
        category_id: form.category_id ? Number(form.category_id) : undefined,
        category_code: category?.code,
        category_name: category?.name,
        warehouse_id: Number(form.warehouse_id),
        description: form.description,
        status: Number(form.status || 1),
      };

      if (isEditMode.value) {
        await incomeService.updateIncome(incomeId.value, payload);
        await Swal.fire(t("income_page.success"), t("income_page.income_updated_successfully"), "success");
      } else {
        await incomeService.createIncome(payload);
        await Swal.fire(t("income_page.success"), t("income_page.income_created_successfully"), "success");
      }

      router.push("/income-list");
    } catch (error) {
      console.error("Error saving income:", error);
      await Swal.fire(t("income_page.error"), t("income_page.failed_to_save_income"), "error");
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(async () => {
    await fetchBaseData();
    if (isEditMode.value) {
      await fetchIncomeDetails();
    } else {
      await fetchNextRef();
    }
  });

  return {
    referenceno,
    date,
    categories,
    warehouses,
    form,
    categoryForm,
    isLoading,
    isCategorySaving,
    isEditMode,
    isCategoryEditMode,
    fetchNextRef,
    openCategoryModal,
    updateCategoryField,
    generateCategoryCode,
    submitCategory,
    submitIncome,
  };
}
